'use client';

import { useState, useEffect, useCallback } from 'react';
import { messaging } from '@/firebase';
import { onMessage, Unsubscribe } from 'firebase/messaging';
import toast from 'react-hot-toast';
import { useDispatch } from 'react-redux';
import { addNotification } from '@/store/notificationsSlice';
import { getNotificationData } from '@/lib/getNotificationData';

const DEFAULT_CREATED_AT = 'just now';

export function useNotifications() {
  const dispatch = useDispatch();

  const [permission, setPermission] = useState<NotificationPermission>(() => {
    if (typeof window !== 'undefined' && 'Notification' in window) {
      return Notification.permission;
    }
    return 'default';
  });

  const [supported, setSupported] = useState<boolean>(() => {
    return (
      typeof window !== 'undefined' &&
      'Notification' in window &&
      typeof Notification.requestPermission === 'function'
    );
  });

  /**
   * Request notification permission from the user
   */
  const requestPermission = useCallback(async () => {
    if (!supported) {
      console.warn('Notifications not supported in this browser');
      return 'denied';
    }

    try {
      const result = await Notification.requestPermission();
      setPermission(result);
      return result;
    } catch (err) {
      console.error('Failed to request notification permission:', err);
      setPermission('denied');
      setSupported(false);
      return 'denied';
    }
  }, [supported]);

  /**
   * Handle incoming foreground FCM messages
   */
  useEffect(() => {
    let unsubscribe: Unsubscribe | null = null;

    if (!supported || permission !== 'granted') return;

    const setupForegroundListener = async () => {
      try {
        const fcm = await messaging();

        if (!fcm) {
          console.warn('FCM instance is not available');
          return;
        }

        unsubscribe = onMessage(fcm, (payload) => {
          try {
            const { title, body } = payload.notification ?? {};
            if (title) toast.success(title, { duration: 6000 });

            dispatch(
              addNotification({
                body: body || '',
                data: getNotificationData(payload.data),
                created_at: DEFAULT_CREATED_AT,
                is_read: false,
              })
            );
          } catch (err) {
            console.error('Error handling foreground FCM message:', err);
          }
        });
      } catch (err) {
        console.error('FCM foreground setup failed:', err);
        setSupported(false);
      }
    };

    setupForegroundListener();

    return () => {
      if (unsubscribe) {
        try {
          unsubscribe();
        } catch (err) {
          console.error('Failed to unsubscribe FCM listener:', err);
        }
      }
    };
  }, [permission, dispatch, supported]);

  /**
   * Handle background messages from Service Worker
   */
  useEffect(() => {
    if (
      typeof window === 'undefined' ||
      typeof navigator === 'undefined' ||
      !('serviceWorker' in navigator)
    )
      return;

    const handler = (e: MessageEvent) => {
      try {
        if (e.data?.type !== 'ZAMZAM_FCM_BACKGROUND_MESSAGE') return;

        console.log(e.data?.notification.body);
        dispatch(
          addNotification({
            body: e.data.payload?.notification?.body || '',
            data: getNotificationData(e.data.payload?.data),
            created_at: DEFAULT_CREATED_AT,
            is_read: false,
          })
        );
      } catch (err) {
        console.error('Failed to process background notification:', err);
      }
    };

    try {
      navigator.serviceWorker.addEventListener('message', handler);
    } catch (err) {
      console.warn('Failed to bind SW event listener:', err);
    }

    return () => {
      try {
        navigator.serviceWorker.removeEventListener('message', handler);
      } catch (err) {
        console.warn('Failed to unbind SW event listener:', err);
      }
    };
  }, [dispatch]);

  /**
   * Watch for permission changes (fallback using interval)
   */
  useEffect(() => {
    if (!supported) return;

    const checkPermissionChange = () => {
      if (Notification.permission !== permission) {
        setPermission(Notification.permission);
      }
    };

    const interval = setInterval(checkPermissionChange, 5000);

    return () => clearInterval(interval);
  }, [permission, supported]);

  return {
    permission,
    supported,
    requestPermission,
  };
}
